
import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import YardDropdown from "../components/YardDropdown";
import yardsData from "../assets/yards.json";
import { FaSort, FaSortAmountUpAlt, FaSortAmountDown } from 'react-icons/fa';
import { usePortalAccess } from '../context/PortalAccessContext';

export default function AlertsDashboard() {
  const { access } = usePortalAccess();
  const portalEmail = (access?.email || '').trim();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [alertTypeFilter, setAlertTypeFilter] = useState(''); // New filter for alert type
  const [yardFilter, setYardFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const navigate = useNavigate();

  const getAlertTypeLabel = (type) => {
    if (type === 'NETWORK_OUTAGE') return 'Network Outage';
    if (type === 'SLOW_PERFORMANCE') return 'Slow Performance';
    return type || 'N/A';
  };

  useEffect(() => {
    const fetchData = async () => {
      const serverUrl = process.env.REACT_APP_API_SERVER_URL || 'http://10.153.28.40:8080';
      try {
        const latestParams = portalEmail ? { params: { portal_email: portalEmail } } : {};
        const [alertsRes, latestRes] = await Promise.all([
          axios.get(`${serverUrl}/maiApi/alerts`),
          axios.get(`${serverUrl}/maiApi/latest-android-data`, latestParams).catch(() => ({ data: [] }))
        ]);
      
      
        const latestByAndroidId = new Map();
        (latestRes.data || []).forEach(item => {
          // prefer the latest by date_time if duplicates
          const existing = latestByAndroidId.get(item.android_id);
          if (!existing || new Date(item.date_time) > new Date(existing.date_time)) {
            latestByAndroidId.set(item.android_id, item);
          }
        });

        const uniqueAlertsMap = new Map();
        alertsRes.data.forEach(alert => {
          // You might want to refine this logic:
          // For example, if you want the LATEST alert for an Android ID:
          // if (!uniqueAlertsMap.has(alert.android_id) || new Date(alert.date_time) > new Date(uniqueAlertsMap.get(alert.android_id).date_time)) {
          //   uniqueAlertsMap.set(alert.android_id, alert);
          // }

          // For now, let's keep the FIRST alert encountered for each Android ID.
          // This prevents duplicates based purely on Android ID.
          const latestForDevice = latestByAndroidId.get(alert.android_id);
          const merged = {
            ...alert,
            yard_number: alert.yard_number ?? latestForDevice?.yard_number ?? null
          };
          // compute display string for yard
          const yardMeta = yardsData.find(y => y.Yard === merged.yard_number);
          merged.yard_display = yardMeta ? `${yardMeta.City}, ${yardMeta.State}  ${yardMeta.Yard}` : '';

          const existing = uniqueAlertsMap.get(alert.android_id);
          if (!existing) {
            uniqueAlertsMap.set(alert.android_id, merged);
          } else {
            const a = new Date(merged.alert_time).getTime();
            const b = new Date(existing.alert_time).getTime();
            // keep the earliest alert_time
            if (!isNaN(a) && (isNaN(b) || a > b)) {
              uniqueAlertsMap.set(alert.android_id, merged);
            }
          }
        });
        const uniqueAlerts = Array.from(uniqueAlertsMap.values());
        setAlerts(uniqueAlerts); // Set the state with unique alerts
       
        } catch (err) {
        console.error("Failed to fetch alerts data:", err);
        setError("Could not load alerts from the server. Please check the connection.");
      } finally {
        setLoading(false);
      }
    };

    fetchData(); // Fetch immediately
    const interval = setInterval(fetchData, 20000); // Refresh every 20 seconds
    return () => clearInterval(interval);
  }, [portalEmail]);

  // Sorting logic (supports string, number, date, and derived duration)
  const sortedAlerts = React.useMemo(() => {
    const toMillis = (dateLike) => {
      if (!dateLike) return 0;
      const t = new Date(dateLike).getTime();
      return isNaN(t) ? 0 : t;
    };
    const computeDurationMs = (alert) => {
      const t = toMillis(alert.alert_time);
      const now = Date.now();
      return t ? Math.max(0, now - t) : 0;
    };

    let sortableItems = [...alerts];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let valA;
        let valB;

        if (sortConfig.key === 'durationMs') {
          valA = computeDurationMs(a);
          valB = computeDurationMs(b);
        } else if (sortConfig.key === 'alert_time') {
          valA = toMillis(a.alert_time);
          valB = toMillis(b.alert_time);
        } else {
          valA = a[sortConfig.key];
          valB = b[sortConfig.key];
        }

        // Normalize undefined/null for comparison
        if (valA == null && valB == null) return 0;
        if (valA == null) return sortConfig.direction === 'ascending' ? 1 : -1;
        if (valB == null) return sortConfig.direction === 'ascending' ? -1 : 1;

        // Numeric compare when both are numbers
        if (typeof valA === 'number' && typeof valB === 'number') {
          if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
          if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
          return 0;
        }

        // Date stored as millis (already handled), or strings
        const aStr = String(valA).toLowerCase();
        const bStr = String(valB).toLowerCase();
        if (aStr < bStr) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [alerts, sortConfig]);

  // Apply filters
  const filteredAlerts = sortedAlerts
    .filter(alert => 
      (alert.details || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (alert.android_id || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (alert.yard_display || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (alert.alert_type || '').toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter(alert => 
      alertTypeFilter ? alert.alert_type === alertTypeFilter : true
    )
    .filter(alert => {
      if (!yardFilter) return true;
      const yard = yardsData.find(y => y.Yard === alert.yard_number);
      const yardFullName = yard ? `${yard.City}, ${yard.State}` : "";
      return yardFullName === yardFilter;
    });

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FaSort className="inline ml-1 opacity-80" title="Click to sort" />;
    }
    return sortConfig.direction === 'ascending'
      ? <FaSortAmountUpAlt className="inline ml-1" title="Ascending" />
      : <FaSortAmountDown className="inline ml-1" title="Descending" />;
  };

  return (
    <div className="p-8">
      <h1 className="text-3xl font-semibold mb-6 text-center">All Alerts</h1>

      {/* Filter controls */}
      <div className="mb-4 flex flex-wrap gap-4 items-center">
        <input
          type="text"
          placeholder="Search by Android Id"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full sm:w-1/3 p-2 border border-gray-300 rounded"
        />
        <select
          value={alertTypeFilter}
          onChange={(e) => setAlertTypeFilter(e.target.value)}
          className="w-full sm:w-1/4 p-2 border border-gray-300 rounded bg-white"
        >
          <option value="">All Alert Types</option>
          <option value="SLOW_PERFORMANCE">Slow Performance</option>
          <option value="NETWORK_OUTAGE">Network Outage</option>
          
        </select>
        <YardDropdown
          value={yardFilter}
          onChange={setYardFilter}
          className="w-full sm:w-1/3"
        />
      </div>

      {/* Alerts Data Table */}
      <div className="overflow-x-auto">
        <table className="w-full border border-gray-300 text-center table-auto">
          <thead className="bg-blue-600 text-white"> {/* Using red for alerts to differentiate */}
            <tr>
              <th className="p-3 border">
                Alert Type
              </th>
              <th className="p-3 border">
                Yard
              </th>
              <th className="p-3 border">
                Android ID
              </th>
              <th className="p-3 border cursor-pointer" onClick={() => requestSort('alert_time')}>
                Date {getSortIcon('alert_time')}
              </th>
              
              {/*<th className="p-3 border cursor-pointer" onClick={() => requestSort('durationMs')}>
                Duration {getSortIcon('durationMs')}
              </th>*/}
              <th
                className= "p-3 border">
                  Uptime
              </th>
              <th className="p-3 border">
                Last HeartBeat Time
              </th>
              
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="p-4 text-center">Loading alerts...</td></tr>
            ) : error ? (
              <tr><td colSpan="6" className="p-4 text-center text-red-600">{error}</td></tr>
            ) : filteredAlerts.length > 0 ? (
              filteredAlerts.map((alert) => (
                <tr key={alert.id || alert.android_id} className="border-t hover:bg-red-50">
                  <td className="p-3 border">{getAlertTypeLabel(alert.alert_type)}</td>
                  <td className="p-3 border">{alert.yard_display || 'N/A'}</td>
                  <td className="p-3 border">{alert.android_id || 'N/A'}</td>
                  <td className="p-3 border"> {alert.alert_time ? new Date(alert.alert_time).toLocaleString() : 'N/A'}</td>
                 {/*} <td className="p-3 border">{ alert.alert_time ? (() => { 
                    const now=new Date();
                    const alertTime = new Date(alert.alert_time);
                    const diffMs = now - alertTime;
                    const diffMins = Math.floor(diffMs / 60000);
                    const hours = Math.floor(diffMins / 60);
                    const mins = diffMins % 60;
                    return `${hours}h ${mins}m`;
                  })() : 'N/A' } </td>*/}
                 <td className="p-3 border">
  {alert.uptime_minutes ? (() => {
    const mins =alert.uptime_minutes;
    if (mins <60) return `Online since last ${mins} mins`;
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
        return minutes === 0
          ? `Online for ${hours} h`
          : `Online for ${hours} h ${minutes} min`;
      })()
    : 'N/A'}
  
</td>

<td className="p-3 border">
  {alert.last_updated_time ? (() => {
    const now = new Date();
    const hb = new Date(alert.last_updated_time);
    const diffMs = now - hb; // difference in milliseconds

    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec} sec ago`;

    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min ago`;

    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour} hr${diffHour > 1 ? 's' : ''} ago`;

    const diffDay = Math.floor(diffHour / 24);
    return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  })() : 'N/A'}
</td>  
                </tr>
              ))
            ) : (
              <tr><td colSpan="6" className="p-4 text-center">No alerts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
